import Image from 'next/image'
import { memo, useState } from 'react'

import { BASE_URL } from '@/shared/lib/const/env'
import { Text } from '@/shared/ui'
import { Notification } from '@/shared/ui/Notification'

import { useProductGiftForBasketItem } from '../../hooks/use-product-gift-for-basket-item'
import { GiftsCatalogModal } from '../gifts-catalog-modal/gifts-catalog-modal'
import { GiftsPackagesModal } from '../gifts-packages-modal/gifts-packages-modal'

import cl from './product-gift-for-basket-item.module.scss'

type ProductGiftForBasketItemProps = {
	productId: number
}

export const ProductGiftForBasketItem = memo((props: ProductGiftForBasketItemProps) => {
	const { productId } = props

	// НОВАЯ АРХИТЕКТУРА: используем новый хук с изолированными данными
	const {
		oneGifts = [],
		allGifts = [],
		selectedGift,
		selectedGiftCount,
		removeGift,
		isAutoGiftsExcluded,
		excludeAutoGifts,
		includeAutoGifts,
		allPackages = [],
		selectedPackageIndex,
		hasMultiplePackages
	} = useProductGiftForBasketItem({ productId })

	// Локальное состояние модалок для каждого товара
	const [isOneGiftsModalOpen, setOneGiftsModalOpen] = useState(false)
	const [isPackagesModalOpen, setPackagesModalOpen] = useState(false)

	// Показываем блок если: есть выбранный подарок, есть one-подарки, есть автоподарки, или есть доступные пакеты
	if (!selectedGift && !oneGifts.length && !allGifts.length && !allPackages.length) return null

	return (
		<>
			{selectedGift && (
				<div className={cl.root}>
					<div className={cl.root__image}>
						<Image
							className={cl.root__giftImage}
							src="https://storage.yandexcloud.net/tf-bitrix-upload/iport/public/assets/features/cart/gift/gift.png"
							width={26}
							height={26}
							alt="Подарок"
						/>
						<Image src={BASE_URL + selectedGift.IMAGE} fill alt={selectedGift.TITLE} />
					</div>
					<div className={cl.root__main}>
						<div className={cl.root__info}>
							<div>
								<Text className={cl.root__name}>{selectedGift.TITLE}</Text>
								<Text className={cl.root__quantity}>{selectedGiftCount} шт.</Text>
							</div>
							<Text size="5" weight="semibold">
								Подарок
							</Text>
						</div>
						<div className={cl.root__buttons}>
							<button className={cl.deleteButton} onClick={removeGift} aria-label="Удалить подарок">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
									<g clipPath="url(#clip0_25804_14414)">
										<path
											d="M18.295 7.115C18.6844 6.72564 18.6844 6.09436 18.295 5.705C17.9056 5.31564 17.2744 5.31564 16.885 5.705L12 10.59L7.115 5.705C6.72564 5.31564 6.09436 5.31564 5.705 5.705C5.31564 6.09436 5.31564 6.72564 5.705 7.115L10.59 12L5.705 16.885C5.31564 17.2744 5.31564 17.9056 5.705 18.295C6.09436 18.6844 6.72564 18.6844 7.115 18.295L12 13.41L16.885 18.295C17.2744 18.6844 17.9056 18.6844 18.295 18.295C18.6844 17.9056 18.6844 17.2744 18.295 16.885L13.41 12L18.295 7.115Z"
											fill="#BDBDBD"
										/>
									</g>
									<defs>
										<clipPath id="clip0_25804_14414">
											<rect width="24" height="24" fill="white" />
										</clipPath>
									</defs>
								</svg>
							</button>
						</div>
						{oneGifts.length > 1 ? (
							<Notification
								type="success"
								message="Вы участвуете в акции «Подарок при покупке»"
								actionLabel="Заменить подарок"
								actionOnClick={() => setOneGiftsModalOpen(true)}
							/>
						) : (
							<Notification type="success" message="Вы участвуете в акции «Подарок при покупке»" />
						)}
					</div>
				</div>
			)}

			{/* Если есть one-подарки, но ничего не выбрано - показываем кнопку выбора */}
			{oneGifts.length > 0 && !selectedGift && (
				<div className={cl.root}>
					<div className={cl.root__main}>
						<div className={cl.root__info}>
							<Text size="5" weight="semibold">
								Подарок к покупке
							</Text>
							<Text className={cl.root__name}>Выберите подарок</Text>
						</div>
						<Notification
							type="success"
							message="Вы участвуете в акции «Подарок при покупке»"
							actionLabel="Выбрать подарок"
							actionOnClick={() => setOneGiftsModalOpen(true)}
						/>
					</div>
				</div>
			)}

			{/* Управление auto-подарками */}
			{isAutoGiftsExcluded && !selectedGift && (
				<div className={cl.root}>
					<div className={cl.root__main}>
						<div className={cl.root__info}>
							<Text size="5" weight="semibold">
								Подарок к покупке
							</Text>
							{allPackages.length > 0 && <Text className={cl.root__name}>Выберите пакет подарков</Text>}
						</div>
						{allPackages.length > 0 ? (
							<Notification
								type="success"
								message="Вы участвуете в акции «Подарок при покупке»"
								actionLabel="Выбрать подарок"
								actionOnClick={() => {
									includeAutoGifts()
									setPackagesModalOpen(true)
								}}
							/>
						) : (
							<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
								<Text>Авто‑подарки скрыты</Text>
								<button className={cl.deleteButton} onClick={includeAutoGifts} aria-label="Вернуть авто‑подарки">
									Вернуть
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Если есть пакеты, но ничего не выбрано - показываем кнопку выбора */}
			{allPackages.length > 0 && typeof selectedPackageIndex !== 'number' && !isAutoGiftsExcluded && !selectedGift && (
				<div className={cl.root}>
					<div className={cl.root__main}>
						<div className={cl.root__info}>
							<Text size="5" weight="semibold">
								Подарок к покупке
							</Text>
							<Text className={cl.root__name}>Выберите пакет подарков</Text>
						</div>
						<Notification
							type="success"
							message="Вы участвуете в акции «Подарок при покупке»"
							actionLabel="Выбрать подарок"
							actionOnClick={() => setPackagesModalOpen(true)}
						/>
					</div>
				</div>
			)}

			{/* Показываем auto-подарки (all) как полноценные карточки подарка с большим изображением и плашкой */}
			{!!allGifts.length && !isAutoGiftsExcluded && !selectedGift && (
				<div style={{ position: `relative` }}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
						<button className={cl.deleteButton} onClick={excludeAutoGifts} aria-label="Удалить пакет подарков">
							<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
								<g clipPath="url(#clip0_25804_14414)">
									<path
										d="M18.295 7.115C18.6844 6.72564 18.6844 6.09436 18.295 5.705C17.9056 5.31564 17.2744 5.31564 16.885 5.705L12 10.59L7.115 5.705C6.72564 5.31564 6.09436 5.31564 5.705 5.705C5.31564 6.09436 5.31564 6.72564 5.705 7.115L10.59 12L5.705 16.885C5.31564 17.2744 5.31564 17.9056 5.705 18.295C6.09436 18.6844 6.72564 18.6844 7.115 18.295L12 13.41L16.885 18.295C17.2744 18.6844 17.9056 18.6844 18.295 18.295C18.6844 17.9056 18.6844 17.2744 18.295 16.885L13.41 12L18.295 7.115Z"
										fill="#BDBDBD"
									/>
								</g>
								<defs>
									<clipPath id="clip0_25804_14414">
										<rect width="24" height="24" fill="white" />
									</clipPath>
								</defs>
							</svg>
						</button>
					</div>
					{allGifts
						.filter(g => g.PRODUCT_IDS?.includes(productId))
						.map(g => (
							<div key={g.ID} className={cl.root}>
								<div className={cl.root__image}>
									<Image src={BASE_URL + g.IMAGE} fill alt={g.TITLE} />
								</div>
								<div className={cl.root__main}>
									<div className={cl.root__info}>
										<div>
											<Text className={cl.root__name}>{g.TITLE}</Text>
											<Text className={cl.root__quantity}>{selectedGiftCount} шт.</Text>
										</div>
										<Text size="5" weight="semibold" className={cl.root__info_buttonText}>
											Подарок
										</Text>
									</div>
								</div>
							</div>
						))}
					<Notification
						type="success"
						message="Вы участвуете в акции «Подарок при покупке»"
						{...(hasMultiplePackages && {
							actionLabel: 'Изменить подарок',
							actionOnClick: () => setPackagesModalOpen(true)
						})}
					/>
				</div>
			)}

			{/* Модалка для one-подарков - рендерим только если у ЭТОГО товара есть oneGifts */}
			{oneGifts.length > 0 && (
				<GiftsCatalogModal
					productId={productId}
					isOpen={isOneGiftsModalOpen}
					onClose={() => setOneGiftsModalOpen(false)}
					gifts={oneGifts}
				/>
			)}

			{/* Модалка для пакетов - рендерим только если у ЭТОГО товара есть allPackages */}
			{allPackages.length > 0 && (
				<GiftsPackagesModal
					productId={productId}
					isOpen={isPackagesModalOpen}
					onClose={() => setPackagesModalOpen(false)}
				/>
			)}
		</>
	)
})
